import * as React from 'react'
import Box from '@mui/material/Box'
import { Navigate } from 'react-router-dom'
import InformationHeader from 'vendors/components/vendorInformation/InformationHeader'
import InformationForm from 'vendors/components/vendorInformation/InformationForm'
import { useVendorWizard } from 'vendors/vendorWizard/VendorWizardContext'
import { useGetVendorDetailsByBusinessId, useAddVendor } from 'vendors/queries/useVendorQuery'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from 'proposals/components/LoadingButton'
import VendorInformationVendorContact from 'vendors/vendorWizard/vendorInformationStep/VendorInformationVendorContact'
import { useTranslation } from 'react-i18next'
import { useGetCompanyByBusinessId } from 'vendors/vendorWizard/vendorInformationStep/queries/useCompanyQueries'

type VendorUserInputErrors = {
  displayName: string
  [key: string]: string
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type VendorInformationStepProps = {
  baseUrl: string
}

const VendorInformationStep: React.FC<VendorInformationStepProps> = ({ baseUrl }) => {
  const { vendorInfo, setVendorInfo, company, approvedRepresentationRightBusinessId, initVendorInfo } =
    useVendorWizard()
  const { t } = useTranslation()
  const initializedVendorId = React.useRef<string | null>(null)

  const {
    isPending: isLoading,
    isError,
    data: vendorDetails
  } = useGetVendorDetailsByBusinessId(company?.businessId ?? '')

  React.useEffect(() => {
    if (!vendorDetails || initializedVendorId.current === vendorDetails.id) {
      return
    }

    initializedVendorId.current = vendorDetails.id
    initVendorInfo(vendorDetails)
  }, [initVendorInfo, vendorDetails])

  const {
    mutate: addVendor,
    isError: isAddVendorError,
    isPending: isAddVendorLoading,
    isSuccess: isAddVendorSuccess,
    data: vendorId
  } = useAddVendor()

  const [errors, setErrors] = React.useState<VendorUserInputErrors>({
    displayName: ''
  })

  const onNextClick = () => {
    const ccEmail = vendorInfo.ccEmail ?? ''
    if (ccEmail !== '' && !EMAIL_REGEX.test(ccEmail)) {
      setErrors((oldState) => ({ ...oldState, ccEmail: t('invalid-email') }))
      return
    }

    const payload = {
      businessId: vendorDetails?.businessId ?? company?.businessId ?? '',
      name: vendorDetails?.name ?? company?.name ?? '',
      ...vendorInfo,
      //30.5.2023 - 10583 GLOBALIZATION - hardcoded language and region
      language: 'fi',
      region: 'FI'
    }

    addVendor(payload)
  }

  // Vendor Wizard will work only for FI region for now 11.01.2024
  const { isLoading: isCompanyLoading } = useGetCompanyByBusinessId(company?.businessId ?? '', 'FI')

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setVendorInfo({ ...vendorInfo, [name]: value })

    if (errors[name] !== '') {
      setErrors((oldState) => ({ ...oldState, [name]: '' }))
    }
  }

  const handleDecidingRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVendorInfo({ ...vendorInfo, [event.target.name]: event.target.checked })
  }

  const handleReceiveRequestForProposalsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVendorInfo({ ...vendorInfo, [event.target.name]: event.target.checked })
  }

  const handleBlurValidation = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    const errorMessage = value === '' ? t('required-field') : ''
    setErrors((oldState) => ({ ...oldState, [name]: errorMessage }))
  }

  const handleCcEmailBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target
    const errorMessage = value !== '' && !EMAIL_REGEX.test(value) ? t('invalid-email') : ''
    setErrors((oldState) => ({ ...oldState, ccEmail: errorMessage }))
  }

  if (!company?.businessId || approvedRepresentationRightBusinessId !== company.businessId) {
    return <Navigate to={`${baseUrl}/company`} replace />
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || isAddVendorError) {
    return <Navigate to="/error" replace />
  }

  if (isAddVendorSuccess) {
    return <Navigate to={`${baseUrl}/${vendorId}/contact`} replace />
  }

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <InformationHeader
        companyName={vendorDetails?.name || company?.name || ''}
        businessId={vendorDetails?.businessId || company?.businessId || ''}
      />
      <InformationForm
        values={vendorInfo}
        onChange={handleValueChange}
        onDecidingRoleChange={handleDecidingRoleChange}
        onReceiveRequestForProposalsChange={handleReceiveRequestForProposalsChange}
        onBlur={handleBlurValidation}
        onCcEmailBlur={handleCcEmailBlur}
        errors={errors}
        isCompanyLoading={isCompanyLoading}
      />
      <VendorInformationVendorContact
        values={vendorInfo}
        onChange={handleValueChange}
        onDecidingRoleChange={handleDecidingRoleChange}
        onReceiveRequestForProposalsChange={handleReceiveRequestForProposalsChange}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <LoadingButton
          disabled={vendorInfo.displayName.length < 1 || Boolean(errors.ccEmail)}
          onClick={onNextClick}
          isLoading={isAddVendorLoading || isLoading}
        >
          {t('next')}
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default VendorInformationStep
